import { useState } from 'react';
import useSWR, { mutate } from 'swr';
import { CcRequest, CcRequestSubmission } from 'types';
import api from 'utils/api';

export function useSubmitCollabRequest() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (payload: CcRequestSubmission) => {
    setIsLoading(true);
    setError(null);

    try {
      const result: CcRequest = await api.collabClub.submitCollabRequest({
        data: payload,
      });
      const optimisticUpdate = async (currentData: CcRequest[] | undefined) => {
        if (!currentData) return [result];
        return [...currentData, result];
      };
      mutate('collab-requests', optimisticUpdate, false);

      setIsLoading(false);
      return { success: true, data: result };
    } catch (err: any) {
      console.error('Failed to submit collaboration request:', err);
      setIsLoading(false);
      setError(err?.data?.message || err.message || 'Failed to submit collaboration request');
      return {
        success: false,
        error: err?.data?.message || err.message || 'Failed to submit collaboration request',
      };
    }
  };

  return {
    submit,
    isLoading,
    error,
    clearError: () => setError(null),
  };
}

export function useCollabRequests(profileId?: number) {
  const {
    data,
    error,
    mutate: refetch,
  } = useSWR<CcRequest[]>(
    profileId ? `collab-requests-current-month-${profileId}` : null,
    profileId
      ? async () => {
          const requests: CcRequest[] = await api.collabClub.getProfileCurrentMonthRequests({
            id: profileId,
          });
          return requests;
        }
      : null,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 5 * 60 * 1000, // 5 minutes
    }
  );

  return {
    requests: data || [],
    hasRequests: (data || []).length > 0,
    isLoading: !data && !error,
    error,
    refetch,
  };
}
